import React, { useState, useEffect, useCallback } from 'react';
import { FolderOpen, Download, Printer, Send, FileText, CheckCircle, Loader, Eye, UploadCloud, Clock, AlertTriangle } from 'lucide-react';
import DocUploadModal from './DocUploadModal';
import MyDmsPortalTab from './MyDmsPortalTab';
import FinePayPanel from './FinePayPanel';

// "My Docs" — the driver's personal document shelf. Docs are pushed from the
// Engine (Documents page) under a category; each is unread (red dot) until the
// driver engages with it (View / Download / Print / Forward), which stamps a
// read-receipt time/day the admin can see. Buttons: View · Download · Print · Forward.

const CATEGORY_ORDER = ['Fines', 'Alerts', 'Operations', 'Safety', 'Briefings', 'Personal', 'Contracts'];
const API = '/.netlify/functions/driver-docs-sb';

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

// Shown every time a driver opens a fine notice: the copy on their shelf is the one
// issued to the company, and paying it would pay the wrong notice.
const FINE_WARNING = 'Do not pay this notice. Another notice will be mailed to you in your name, and you can pay using that notice.';

const fmtMoney = (n) => (n == null ? '' : Number(n).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }));
const fmtDay = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function b64ToBlob(base64, type) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return new Blob([bytes], { type: type || 'application/pdf' });
}

export default function MyDocsPortalTab({ shift, onCountChange, showOnFile, onDmsCountChange }) {
  const [docs, setDocs]       = useState(null);   // null = loading
  const [busyId, setBusyId]   = useState('');
  const [error, setError]     = useState('');
  const [fwdFor, setFwdFor]   = useState('');     // doc id whose forward box is open
  const [fwdTo, setFwdTo]     = useState('');
  const [fwdMsg, setFwdMsg]   = useState('');
  const [rejFor, setRejFor]   = useState('');     // doc id whose reject-reason box is open
  const [rejReason, setRejReason] = useState('');
  const [uploads, setUploads] = useState([]);     // documents this driver has sent to the office
  const [showUpload, setShowUpload] = useState(false);
  const [fineGate, setFineGate] = useState(null);  // { run } — open a fine only after the warning

  const api = useCallback(async (payload) => {
    const res = await fetch(API, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  }, []);

  const load = useCallback(async () => {
    try {
      const [d, u] = await Promise.all([
        api({ action: 'getMyDocs' }),
        api({ action: 'listMyUploads' }),
      ]);
      if (d && d.success) {
        setDocs(d.documents || []);
        onCountChange && onCountChange(d.unread || 0);
      } else {
        setDocs([]); setError(d?.error || 'Could not load documents');
      }
      if (u && u.success) setUploads(u.uploads || []);
    } catch (e) { setDocs([]); setError(e.message); }
  }, [api, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const fetchBytes = async (doc) => {
    const d = await api({ action: 'getDocFile', id: doc.id });
    if (!d || !d.success) throw new Error(d?.error || 'Could not open document');
    return { blob: b64ToBlob(d.base64, d.contentType), filename: d.filename };
  };

  const onView = async (doc) => {
    // Open the tab synchronously (before the await) so mobile popup blockers
    // don't swallow it. Viewing counts as opening → stamps the read receipt.
    const w = window.open('', '_blank');
    setBusyId(doc.id + ':vw'); setError('');
    try {
      const { blob } = await fetchBytes(doc);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      await load();
    } catch (e) { if (w) w.close(); setError(e.message); } finally { setBusyId(''); }
  };

  const onDownload = async (doc) => {
    setBusyId(doc.id + ':dl'); setError('');
    try {
      const { blob, filename } = await fetchBytes(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'document.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };

  const onPrint = async (doc) => {
    setBusyId(doc.id + ':pr'); setError('');
    try {
      const { blob } = await fetchBytes(doc);
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
      iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = '0';
      iframe.src = url;
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { window.open(url, '_blank'); }
        setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60000);
      };
      document.body.appendChild(iframe);
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };

  // A fine notice opens only through the "do not pay" warning. The warning's button is
  // itself a tap, so View's window.open still counts as a user gesture on iOS.
  // A toll notice the driver pays has no such warning — its card says how to pay it.
  const gated = (doc, fn) => (doc.fine && !doc.pay ? setFineGate({ run: () => fn(doc) }) : fn(doc));

  const openForward = (doc) => { setFwdFor(doc.id); setFwdTo(''); setFwdMsg(''); };
  const onForward = async (doc) => {
    if (!fwdTo.trim()) return;
    setBusyId(doc.id + ':fw'); setFwdMsg('');
    try {
      const d = await api({ action: 'forwardDoc', id: doc.id, to: fwdTo.trim() });
      if (d && d.success) { setFwdMsg('Sent ✓'); setTimeout(() => { setFwdFor(''); setFwdMsg(''); }, 1600); await load(); }
      else setFwdMsg(d?.error || 'Send failed');
    } catch (e) { setFwdMsg(e.message); } finally { setBusyId(''); }
  };

  // Accept / reject an offer document. Reject requires the reason box to be open.
  const onRespond = async (doc, response) => {
    if (response === 'accepted' && !window.confirm('Accept this offer? This tells the office you\'d like to proceed — the binding contract is what you\'ll sign next.')) return;
    setBusyId(doc.id + ':resp'); setError('');
    try {
      const d = await api({ action: 'respondToOffer', id: doc.id, response, reason: response === 'rejected' ? rejReason.trim() : '' });
      if (d && d.success) { setRejFor(''); setRejReason(''); await load(); }
      else setError(d?.error || 'Could not submit your response');
    } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };

  // Acknowledge a letter the office asked them to confirm receiving.
  const onAcknowledge = async (doc) => {
    if (!window.confirm(`Acknowledge “${doc.title}”? This confirms to the office that you have received and read it.`)) return;
    setBusyId(doc.id + ':ack'); setError('');
    try {
      const d = await api({ action: 'acknowledgeDoc', id: doc.id });
      if (d && d.success) await load();
      else setError(d?.error || 'Could not acknowledge');
    } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };

  if (docs === null) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={16} /> Loading your documents…</div>;
  }

  const uploadModal = showUpload && (
    <DocUploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />
  );

  // The embedded DMS tab renders its own "Your documents on file" heading and
  // self-hides when the driver has no required documents.
  const onFilePanel = showOnFile ? (
    <div style={{ marginBottom: 22 }}>
      <MyDmsPortalTab embedded shift={shift} onCountChange={onDmsCountChange} />
    </div>
  ) : null;

  if (!docs.length) {
    return (
      <div style={{ width: '100%' }}>
        <SectionTitle />
        <UploadsSection uploads={uploads} onUpload={() => setShowUpload(true)} />
        {onFilePanel}
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)', border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)', marginTop: 18 }}>
          No documents from the office yet. Anything your manager sends you will appear here.
        </div>
        {uploadModal}
      </div>
    );
  }

  // Group by category, in a fixed order (any unknown categories tacked on the end).
  const cats = [...CATEGORY_ORDER, ...[...new Set(docs.map(d => d.category))].filter(c => !CATEGORY_ORDER.includes(c))];

  // Fines go first — the portal banner sends drivers here, and a notice must not sit
  // below the long "documents on file" list.
  const renderCat = (cat) => {
        const group = docs.filter(d => d.category === cat);
        if (!group.length) return null;
        const unread = group.some(d => d.status !== 'opened' || (d.requiresAck && !d.acknowledgedAt));
        return (
          <div key={cat} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>
                {cat}
              </span>
              {unread && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--hp-red)' }} />}
              <span style={{ flex: 1, height: 1, background: 'var(--hp-border)' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {group.map(doc => {
                const opened = doc.status === 'opened';
                return (
                  <div key={doc.id} style={{
                    border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14,
                    background: opened ? 'var(--hp-surface)' : 'rgba(214,69,69,0.05)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <FileText size={18} color="var(--hp-gold)" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          {doc.title}
                          {!opened && <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--hp-red)', flexShrink: 0 }} />}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                          Sent {fmtWhen(doc.assignedAt)}
                          {opened && (
                            <span style={{ color: 'var(--hp-green)', marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <CheckCircle size={11} /> Opened {fmtWhen(doc.openedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {doc.fine && doc.pay && (
                      <>
                        <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
                          {doc.fine.offenceDesc && <div style={{ color: 'var(--hp-text)' }}>{doc.fine.offenceDesc}</div>}
                          {[doc.fine.rego, 'trip ' + fmtDay(doc.fine.offenceDate) + (doc.fine.offenceTime ? ` ${doc.fine.offenceTime}` : '')].filter(Boolean).join(' · ')}
                        </div>
                        <FinePayPanel doc={doc} onChanged={load} />
                      </>
                    )}

                    {doc.fine && !doc.pay && (
                      <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>
                        {doc.fine.offenceDesc && <div>{doc.fine.offenceDesc}</div>}
                        <div style={{ color: 'var(--hp-dim)' }}>
                          {[doc.fine.rego, fmtDay(doc.fine.offenceDate) + (doc.fine.offenceTime ? ` ${doc.fine.offenceTime}` : ''), fmtMoney(doc.fine.amount)].filter(Boolean).join(' · ')}
                        </div>
                        <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 'var(--radius)', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.5)', fontWeight: 700, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                          <AlertTriangle size={14} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} /> {FINE_WARNING}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                      <ActBtn onClick={() => gated(doc, onView)}     busy={busyId === doc.id + ':vw'} icon={<Eye size={13} />}      label="View" primary />
                      <ActBtn onClick={() => gated(doc, onDownload)} busy={busyId === doc.id + ':dl'} icon={<Download size={13} />} label="Download" />
                      <ActBtn onClick={() => gated(doc, onPrint)}    busy={busyId === doc.id + ':pr'} icon={<Printer size={13} />}  label="Print" />
                      <ActBtn onClick={() => openForward(doc)} icon={<Send size={13} />} label="Forward" />
                    </div>

                    {doc.requiresAck && (doc.acknowledgedAt ? (
                      <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius)', fontSize: '0.8rem', fontWeight: 600, background: 'rgba(46,125,50,0.10)', color: 'var(--hp-green)', border: '1px solid var(--hp-border)' }}>
                        ✓ You acknowledged this letter · {fmtWhen(doc.acknowledgedAt)}
                      </div>
                    ) : (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--hp-border)' }}>
                        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                          The office has asked you to confirm you have received this letter. Please read it, then tap <b style={{ color: 'var(--hp-text)' }}>Acknowledge</b>.
                        </div>
                        <button onClick={() => onAcknowledge(doc)} disabled={busyId === doc.id + ':ack'}
                          style={{ padding: '9px 16px', fontSize: '0.82rem', fontWeight: 800, border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-green)', color: '#fff', cursor: 'pointer', opacity: busyId === doc.id + ':ack' ? 0.6 : 1 }}>
                          {busyId === doc.id + ':ack' ? 'Sending…' : 'Acknowledge'}
                        </button>
                      </div>
                    ))}

                    {doc.requiresResponse && (doc.response ? (
                      <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius)', fontSize: '0.8rem', fontWeight: 600,
                        background: doc.response === 'accepted' ? 'rgba(46,125,50,0.10)' : 'rgba(214,69,69,0.08)',
                        color: doc.response === 'accepted' ? 'var(--hp-green)' : 'var(--hp-red)', border: '1px solid var(--hp-border)' }}>
                        {doc.response === 'accepted' ? '✓ You accepted this offer' : '✕ You declined this offer'} · {fmtWhen(doc.respondedAt)}
                        {doc.response === 'accepted' && <div style={{ color: 'var(--hp-dim)', fontWeight: 500, marginTop: 3 }}>The office will prepare your contract to review and sign.</div>}
                        {doc.responseReason && <div style={{ color: 'var(--hp-dim)', fontWeight: 500, marginTop: 3 }}>Reason: {doc.responseReason}</div>}
                      </div>
                    ) : (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--hp-border)' }}>
                        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                          Please review this offer and let us know. <b style={{ color: 'var(--hp-text)' }}>Accepting</b> means you'd like to proceed — the binding contract is what you'll sign next.
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button onClick={() => onRespond(doc, 'accepted')} disabled={busyId === doc.id + ':resp'}
                            style={{ padding: '9px 16px', fontSize: '0.82rem', fontWeight: 800, border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-green)', color: '#fff', cursor: 'pointer', opacity: busyId === doc.id + ':resp' ? 0.6 : 1 }}>
                            {busyId === doc.id + ':resp' ? 'Sending…' : 'Accept offer'}
                          </button>
                          <button onClick={() => { setRejFor(rejFor === doc.id ? '' : doc.id); setRejReason(''); }}
                            style={{ padding: '9px 16px', fontSize: '0.82rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>
                            Reject
                          </button>
                        </div>
                        {rejFor === doc.id && (
                          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input type="text" placeholder="Reason (optional)…" value={rejReason} onChange={e => setRejReason(e.target.value)}
                              style={{ flex: 1, minWidth: 160, padding: '8px 10px', fontSize: '0.82rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)' }} />
                            <button onClick={() => onRespond(doc, 'rejected')} disabled={busyId === doc.id + ':resp'}
                              style={{ padding: '8px 14px', fontSize: '0.8rem', fontWeight: 700, border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-red)', color: '#fff', cursor: 'pointer' }}>
                              {busyId === doc.id + ':resp' ? 'Sending…' : 'Confirm reject'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {fwdFor === doc.id && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                          type="email" placeholder="Send to email…" value={fwdTo}
                          onChange={e => setFwdTo(e.target.value)}
                          style={{ flex: 1, minWidth: 160, padding: '8px 10px', fontSize: '0.82rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)' }}
                        />
                        <button onClick={() => onForward(doc)} disabled={busyId === doc.id + ':fw' || !fwdTo.trim()}
                          style={{ padding: '8px 14px', fontSize: '0.8rem', fontWeight: 700, border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-gold)', color: '#1a1a2e', cursor: 'pointer', opacity: (!fwdTo.trim() || busyId === doc.id + ':fw') ? 0.6 : 1 }}>
                          {busyId === doc.id + ':fw' ? 'Sending…' : 'Send'}
                        </button>
                        <button onClick={() => setFwdFor('')} style={{ padding: '8px 10px', fontSize: '0.8rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>Cancel</button>
                        {fwdMsg && <span style={{ fontSize: '0.75rem', color: fwdMsg.includes('✓') ? 'var(--hp-green)' : 'var(--hp-red)' }}>{fwdMsg}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
  };

  return (
    <div style={{ width: '100%' }}>
      <SectionTitle />
      {renderCat('Fines')}
      <UploadsSection uploads={uploads} onUpload={() => setShowUpload(true)} />
      {onFilePanel}
      {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.8rem', marginBottom: 12 }}>{error}</div>}

      {cats.filter(c => c !== 'Fines').map(renderCat)}
      {uploadModal}
      {fineGate && <FineWarning onClose={() => setFineGate(null)} onContinue={() => { const run = fineGate.run; setFineGate(null); run(); }} />}
    </div>
  );
}

function FineWarning({ onClose, onContinue }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="alertdialog" aria-labelledby="fine-warn-h" onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 400, background: 'var(--hp-surface)', borderRadius: 14, padding: 22, border: '2px solid #d97706', boxShadow: '0 12px 40px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <AlertTriangle size={26} color="#d97706" />
          <div id="fine-warn-h" style={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--hp-text)' }}>Do not pay this notice</div>
        </div>
        <div style={{ fontSize: '0.92rem', color: 'var(--hp-text)', lineHeight: 1.55 }}>
          This is the notice issued to the company. <b>Another notice will be mailed to you</b> in your name, and you can pay using that notice.
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 8 }}>This copy is for your records.</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button onClick={onContinue} style={{ flex: 1, padding: '11px 14px', fontSize: '0.9rem', fontWeight: 800, border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-gold)', color: '#1a1a2e', cursor: 'pointer' }}>
            I understand — open it
          </button>
          <button onClick={onClose} style={{ padding: '11px 14px', fontSize: '0.9rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Upload area + the documents this driver has already sent to the office ─────
function UploadsSection({ uploads, onUpload }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: 14, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
        <UploadCloud size={20} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>Send a document to the office</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>Attach a PDF or take a photo — it goes straight to your compliance file.</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onUpload}>Upload a document</button>
      </div>

      {uploads && uploads.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8 }}>
            Documents you've sent
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {uploads.map(u => {
              const reviewed = u.status === 'reviewed';
              return (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
                  <FileText size={16} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--hp-text)' }}>{u.title}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>Sent {fmtWhen(u.uploadedAt)}</div>
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 700,
                    color: reviewed ? 'var(--hp-green)' : 'var(--hp-dim)' }}>
                    {reviewed ? <><CheckCircle size={12} /> Reviewed</> : <><Clock size={12} /> Received</>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionTitle() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <FolderOpen size={18} color="var(--hp-gold)" />
      <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>My Docs</span>
    </div>
  );
}

function ActBtn({ onClick, busy, icon, label, primary }) {
  return (
    <button onClick={onClick} disabled={busy}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', fontSize: '0.8rem', fontWeight: 700, borderRadius: 'var(--radius)', cursor: 'pointer', opacity: busy ? 0.6 : 1,
        border: primary ? 'none' : '1px solid var(--hp-border)',
        background: primary ? 'var(--hp-gold)' : 'var(--hp-surface)',
        color: primary ? '#1a1a2e' : 'var(--hp-text)' }}>
      {icon} {busy ? '…' : label}
    </button>
  );
}
